import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ProjectData } from '@/types/project';

export const useProjectImages = () => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateProjectImages = async (
    project: ProjectData,
    useRealImages: boolean = true,
    provider: 'openai' | 'huggingface' | 'replicate' = 'huggingface' // Default to most cost-effective
  ): Promise<string[]> => {
    setIsGenerating(true);
    const images: string[] = [];
    
    try {
      // Choose the appropriate edge function based on provider
      const functionName = provider === 'openai' ? 'property-image-generator' : 
                          provider === 'huggingface' ? 'huggingface-image-generator' :
                          'replicate-image-generator';
      
      console.log(`Generating images for ${project.name} using ${provider}`);

      // Enhanced prompts and search queries for real images
      const imageConfig = [
        {
          prompt: `Ultra-modern luxury ${project.propertyType.toLowerCase()} building facade in Mumbai's ${project.area}. ${project.name} by ${project.developer}. Contemporary glass and steel architecture, premium balconies, landscaped entrance, Mumbai skyline background. Photorealistic, architectural photography style, golden hour lighting.`,
          searchQuery: `${project.name} ${project.developer} Mumbai building facade exterior architecture`
        },
        {
          prompt: `Luxurious lobby interior of ${project.name} by ${project.developer}. Modern design with marble floors, contemporary furniture, elegant lighting, double-height ceiling, premium finishes. Mumbai luxury residential lobby.`,
          searchQuery: `${project.name} ${project.developer} lobby interior entrance Mumbai luxury`
        },
        {
          prompt: `Premium amenities area of ${project.name} - infinity swimming pool with city views, modern gym equipment, landscaped gardens, clubhouse area. Mumbai luxury residential amenities.`,
          searchQuery: `${project.name} amenities swimming pool clubhouse facilities Mumbai ${project.developer}`
        },
        {
          prompt: `Spacious modern apartment interior at ${project.name}. ${project.bhkOptions[0]} layout with floor-to-ceiling windows, Mumbai city views, contemporary kitchen, elegant living area, premium finishes.`,
          searchQuery: `${project.name} apartment interior sample flat ${project.developer} Mumbai`
        },
        {
          prompt: `Aerial view of ${project.name} development in Mumbai's ${project.area}. Multiple towers, landscaped grounds, swimming pools, recreational facilities, modern architecture, Mumbai cityscape.`,
          searchQuery: `${project.name} aerial view masterplan ${project.area} Mumbai ${project.developer}`
        }
      ];

      for (let i = 0; i < imageConfig.length; i++) {
        try {
          const { data, error } = await supabase.functions.invoke(functionName, {
            body: { 
              action: 'generate-image',
              prompt: imageConfig[i].prompt,
              searchQuery: imageConfig[i].searchQuery,
              projectName: project.name,
              imageIndex: i,
              useRealImages
            }
          });

          if (error) {
            console.error(`Failed to generate image ${i + 1} with ${provider}:`, error);
            continue;
          }

          if (data?.imageUrl) {
            images.push(data.imageUrl);
            console.log(`Generated image ${i + 1} with ${provider}: ${data.imageUrl}`);
          }
        } catch (error) {
          console.error(`Error generating image ${i + 1} with ${provider}:`, error);
        }
      }
      
      return images;
    } catch (error) {
      console.error(`Error in generateProjectImages with ${provider}:`, error);
      return images;
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    generateProjectImages,
    isGenerating
  };
};